import React, { useState, useContext, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

import { Divider, Drawer, Grid, Typography } from '@material-ui/core';
import { Button, MdSpace, SmSpace, TextField } from 'm2m.components';

import { addAlert } from 'store/modules/snackBar';
import { AditamentoContext, AditamentosContextType, aditamentoInitialState } from '.';
import { FuncaoParticipante, InstrumentoFinanceiro, GrupoAcesso } from 'common/genericTypes';

export const DrawerAprovarRejeitar: React.FC = () => {
  const { openValidationDrawer, setOpenValidationDrawer, selected, setSelected, checked, setChecked, actionType, refreshList } = useContext(
    AditamentoContext
  ) as AditamentosContextType;
  const dispatch = useDispatch();

  const [justification, setJustification] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const typeCapitalized = actionType.charAt(0).toUpperCase() + actionType.slice(1);

  const putAditamentoApprove = useSelector((state: RootState) => state.aditamentosApi.putAditamentoApprove);
  const putAditamentoDisapprove = useSelector((state: RootState) => state.aditamentosApi.putAditamentoDisapprove);
  const isUsuarioOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes);
  const isUsuarioAgenteFiduciario = useSelector((state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.AgenteFiduciario);
  const isUsuarioEscriturador = useSelector((state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.Escriturador);
  const currentParticipanteCnpj = useSelector((state: RootState) => state.auth.usuario.participante.cnpj);

  const getInstrumentoDetalhesByCodigo = useSelector((state: RootState) => state.instrumentoApi.getInstrumentoDetalhesByCodigo);

  const getInstrumentoForValidation = async () => {
    try {
      return await getInstrumentoDetalhesByCodigo(dispatch, selected.codigoInstrumentoFinanceiro, true);
    } catch {
      return undefined;
    }
  };

  const handleSubmitClick = async () => {
    try {
      if (submitting) return;
      setSubmitting(true);
      if (!isUsuarioOperacoes && (isUsuarioAgenteFiduciario || isUsuarioEscriturador)) {
        const instrumento: any = await getInstrumentoForValidation();
        let canApprove = false;

        const solicitanteFuncao = (selected as any)?.solicitanteFuncao || '';
        const solicitanteIsAF =
          solicitanteFuncao === 'AGENTE_FIDUCIARIO' || solicitanteFuncao === FuncaoParticipante.AgenteFiduciario;
        const solicitanteIsEscriturador =
          solicitanteFuncao === 'ESCRITURADOR' || solicitanteFuncao === FuncaoParticipante.Escriturador;

        if (isUsuarioAgenteFiduciario) {
          const counterpartOk = solicitanteIsEscriturador;
          const linkageOk = Boolean(
            instrumento &&
              instrumento.distribuicaoPublica &&
              instrumento.cpfCnpjAgenteFiduciario &&
              currentParticipanteCnpj &&
              instrumento.cpfCnpjAgenteFiduciario === currentParticipanteCnpj
          );
          canApprove = counterpartOk && linkageOk;
        } else if (isUsuarioEscriturador) {
          const counterpartOk = solicitanteIsAF;
          const linkageOk = Boolean(
            instrumento &&
              instrumento.distribuicaoPublica &&
              instrumento.cnpjEscriturador &&
              currentParticipanteCnpj &&
              instrumento.cnpjEscriturador === currentParticipanteCnpj
          );
          canApprove = counterpartOk && linkageOk;
        }
        if (!canApprove) {
          dispatch(
            addAlert({
              message: 'Você não possui permissão para executar esta ação neste aditamento.',
              severity: 'error',
            })
          );
          return;
        }
      }
      let resp;
      if (actionType === 'aprovar') {
        resp = await putAditamentoApprove(dispatch, selected.aditamentoGuid, justification);
      }
      if (actionType === 'rejeitar') {
        resp = await putAditamentoDisapprove(dispatch, selected.aditamentoGuid, justification);
      }
      setChecked(false);
      setSelected(aditamentoInitialState);
      setJustification('');
      await refreshList();
      setOpenValidationDrawer(false);
      dispatch(
        addAlert({
          message: resp ? resp : '',
          severity: 'success',
        })
      );
    } catch (error) {
      dispatch(
        addAlert({
          message: 'Houve um problema ao salvar',
          severity: 'error',
        })
      );
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer anchor="right" open={openValidationDrawer} PaperProps={{ style: { padding: '40px', width: 520 } }}>
      <Grid container>
        <Grid item sm={12}>
          <Typography variant="h3">{`${typeCapitalized} Aditamento`}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          <Typography variant="body2" display={'inline'}>
            {`Tem certeza que deseja ${actionType} o aditamento do instrumento: ${selected.codigoInstrumentoFinanceiro}?  `}
          </Typography>
          <MdSpace />
          <TextField
            label="Justificativa"
            value={justification}
            rows={5}
            onChange={setJustification}
            multiline
            fullWidth
            disabled={submitting}
          />
          <SmSpace />

          <Button
            variant="contained"
            fullWidth
            color={actionType === 'rejeitar' ? 'error' : 'primary'}
            onClick={handleSubmitClick}
            disabled={justification === '' || submitting}
          >
            {submitting ? 'Processando…' : typeCapitalized}
          </Button>
          <SmSpace />
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              setJustification('');
              setOpenValidationDrawer(false);
            }}
            disabled={submitting}
          >
            {'Cancelar'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};
